import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { CircularProgress, Typography } from '@mui/material';
import SimpleLinearRegression from 'ml-regression-simple-linear';


export const RTFOPumpdownFalloffChart = ({ 
  gridView, 
  showSpikeLines, 
  pumpdownData, 
  pumpdownFalloffTimestamps, 
  postPerfFalloffTimestamps, 
  isLoadingPumpdownData,
  pumpdownISIP,
  updatePumpdownISIP,
  postPerfISIP,
  updatePostPerfISIP
}) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

  const darkMode = user?.value?.darkMode;

  const data = useMemo(() => {
    if (currentStage && pumpdownData) {
      return getChartData();
    };

    function getChartData() {
      try {
        // add traces
        let traces = [];

        const pumpdownFalloffData = pumpdownData?.filter(d => (d.timestamp >= pumpdownFalloffTimestamps?.startTimestamp - 60000) && d.timestamp <= pumpdownFalloffTimestamps?.endTimestamp);
        const postPerfFalloffData = pumpdownData?.filter(d => (d.timestamp >= postPerfFalloffTimestamps?.startTimestamp - 60000) && d.timestamp <= postPerfFalloffTimestamps?.endTimestamp);

        // plot pumpdown data
        if (pumpdownFalloffData?.length > 0) {
          const XPumpdown = [];
          const YRatePumpdown = [];
          const YPressurePumpdown = [];
          const startTimestamp = pumpdownFalloffTimestamps?.startTimestamp;

          pumpdownFalloffData.forEach(d => {
            const averagePressure = d.pressure || ((d.leftPumpPressure + d.rightPumpPressure) / 2) || 0; 
            if (averagePressure > 0) {
              XPumpdown.push((d.timestamp - startTimestamp) / 60000);
              YRatePumpdown.push(d.rate);
              YPressurePumpdown.push(averagePressure);
            };
          })
  
          traces.push(
            { // pump pressure
              x: XPumpdown,
              y: YPressurePumpdown,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y1',
              name: 'Pumpdown Pressure (MPa)',
              line: {
                width: 2,
                color: chartColors[2],
                opacity: 1
              },
              hoverinfo: !showSpikeLines && 'none'
            }
          );
          traces.push(
            { // rate
              x: XPumpdown,
              y: YRatePumpdown,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y2',
              name: 'Pumpdown Rate (m3/min)',
              line: {
                width: 2,
                color: chartColors[3],
                opacity: 1
              },
              hoverinfo: !showSpikeLines && 'none'
            }
          );
          
          // calculate fit line
          const regression = new SimpleLinearRegression(XPumpdown?.slice(60, ), YPressurePumpdown?.slice(60, ));
          


          if (regression.intercept > 0) {
            updatePumpdownISIP(regression.intercept);
            // setPumpdownISIP(regression.intercept);
          };

          const XFitPumpdown = [];
          const YFitPumpdown = [];
          for (let time = -1; time <= 10; time++) {
            XFitPumpdown.push(time);
            YFitPumpdown.push(regression.predict(time));
          };

          // const YFitPumpdown = XPumpdown.map(val => regression.predict(val));
          traces.push(
            { // fit line
              x: XFitPumpdown,
              y: YFitPumpdown,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y1',
              name: 'Pumpdown Fit Line',
              line: {
                width: 2,
                color: chartColors[2],
                opacity: 1,
                dash: 'dash'
              },
              marker: {
                color: 'transparent'
              },
              hoverinfo: !showSpikeLines && 'none'
            }
          );
        }

        // plot post perf data
        if (postPerfFalloffData?.length > 0) {
          const XPostPerf = [];
          const YRatePostPerf = [];
          const YPressurePostPerf = [];
          const startTimestamp = postPerfFalloffTimestamps?.startTimestamp;
  
          postPerfFalloffData.forEach(d => {
            const averagePressure = (d.pressure || ((d.leftPumpPressure + d.rightPumpPressure) / 2) || 0); 
            if (averagePressure > 0) {
              XPostPerf.push((d.timestamp - startTimestamp) / 60000);
              YRatePostPerf.push(d.rate);
              YPressurePostPerf.push(averagePressure);
            }
          })
  
          traces.push(
            { // pump pressure
              x: XPostPerf,
              y: YPressurePostPerf,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y1',
              name: 'Post Perf Pressure (MPa)',
              line: {
                width: 2,
                color: chartColors[6],
                opacity: 1
              },
              hoverinfo: !showSpikeLines && 'none'
            }
          );
          traces.push(
            { // rate
              x: XPostPerf,
              y: YRatePostPerf,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y2',
              name: 'Post Perf Rate (m3/min)',
              line: {
                width: 2,
                color: chartColors[7],
                opacity: 1
              },
              hoverinfo: !showSpikeLines && 'none'
            }
          );
          
          // calculate fit line
          const regression = new SimpleLinearRegression(XPostPerf?.slice(parseInt(XPostPerf?.length * 0.5), ), YPressurePostPerf?.slice(parseInt(YPressurePostPerf?.length * 0.5), ));
          
          if (regression.intercept > 0) {
            updatePostPerfISIP(regression.intercept);
          };

          const XFitPostPerf = [];
          const YFitPostPerf = [];
          for (let time = -1; time <= 10; time++) {
            XFitPostPerf.push(time);
            YFitPostPerf.push(regression.predict(time));
          };

          traces.push(
            { // fit line
              x: XFitPostPerf,
              y: YFitPostPerf,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y1',
              name: 'Post Perf Fit Line',
              line: {
                width: 2,
                color: chartColors[6],
                opacity: 1,
                dash: 'dash'
              },
              marker: {
                color: 'transparent'
              },
              hoverinfo: !showSpikeLines && 'none'
            }
          );
        }
  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentStage, postPerfFalloffTimestamps?.endTimestamp, postPerfFalloffTimestamps?.startTimestamp, pumpdownData, pumpdownFalloffTimestamps?.endTimestamp, pumpdownFalloffTimestamps?.startTimestamp, showSpikeLines, updatePostPerfISIP, updatePumpdownISIP]);


  const layout = {
    yaxis: { // pump pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: 'Pressure (MPa)',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      side: 'left'
    },
    yaxis2: { // rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: 'Rate (m3/min)',
        font: {
          size: 12
        },
      },
      range: [0, 10],
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0
    },
    yaxis3: { // depth y axis
      linecolor: 'black',
      showgrid: false,
      color: 'black',
      title: {
        text: 'Depth (m)',
        font: {
          size: 12
        },
      },
      rangemode: 'nonnegative',
      overlaying: 'y',
      side: 'right',
      anchor: 'x'
    },
    yaxis4: { // voltage y axis
      linecolor: chartColors[6],
      showgrid: false,
      color: chartColors[6],
      title: {
        text: 'Voltage',
        font: {
          size: 12
        },
      },
      range: [-1, 1],
      overlaying: 'y',
      side: 'right',
      anchor: 'free',
      position: 1,
      uirevision: 'true'
    },
    xaxis: { // Time x axis
      title: {
        text: 'Time (min)',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      domain: [(gridView ? 0.1 : 0.05), 1],
      range: [-1, 10],
      // zeroline: false
      // showspikes: true,
      // spikethickness: 2,
      // spikedash: "dot",
      // spikecolor: "#999999",
      // spikemode: "across",
    },
    // hovermode: "x",
    // hoverdistance: 10, // Distance to show hover label of data point
    // spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.15 : -0.1),
    },
    shapes: [ 
    ],
    annotations: [
      {
        text: "Pressure Falloff",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.135 : 1.05), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.08 : 1.02), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      pumpdownISIP && {
        text: `<b>Pumpdown ISIP: ${pumpdownISIP?.toFixed(2)} MPa</b>`,
          font: {
            size: 14,
            color: chartColors[2]
            // color: darkMode ? '#eee' : '#333'
          },
        align: 'center',
        x: 0, //position in x domain
        y: pumpdownISIP,
        xref: 'x',
        yref: 'y',
        showarrow: true,
        arrowcolor: chartColors[2],
        // arrowcolor: darkMode ? '#eee' : '#333',
        arrowhead: 3,
        ax: 120,
        ay: 60
      },
      postPerfISIP && {
        text: `<b>Post Perf ISIP: ${postPerfISIP?.toFixed(2)} MPa</b>`,
          font: {
            size: 14,
            color: chartColors[6]
            // color: darkMode ? '#eee' : '#333'
          },
        align: 'center',
        x: 0, //position in x domain
        y: postPerfISIP,
        xref: 'x',
        yref: 'y',
        showarrow: true,
        arrowcolor: chartColors[6],
        // arrowcolor: darkMode ? '#eee' : '#333',
        arrowhead: 3,
        ax: 120,
        ay: -60
      },
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "page",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-ISIP-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <div style={{height: '100%', width: '100%'}}>
      {isLoadingPumpdownData.current ?
        <div style={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
        <Typography style={{marginBottom: 20}}>Downloading pumpdown data...</Typography>
        <CircularProgress />
      </div> :
      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler={true}
        style={{width: '100%', height: '100%'}}
      />}

    </div>
  );

};
